// B3-01 / B3-06: explore mode state for the signed-in person.
//
// One trusted server call tells us whether they are currently inside their
// sample nursery, and whether they are allowed to enter it at all (Starter
// plans only). The same call tidies the sandbox away the moment the real
// nursery genuinely upgrades to Professional.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ExploreStatus {
  exploring: boolean;
  canExplore: boolean;
  realNurseryId: string | null;
  realNurseryName: string | null;
  realPlan: string | null;
  isLoading: boolean;
}

interface RawStatus {
  exploring?: boolean;
  can_explore?: boolean;
  real_nursery_id?: string | null;
  real_nursery_name?: string | null;
  real_plan?: string | null;
}

export function useExploreMode(): ExploreStatus {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["explore-status", user?.id],
    queryFn: async (): Promise<RawStatus | null> => {
      const { data, error } = await supabase.rpc("explore_status" as never);
      if (error) return null;
      return (data ?? null) as RawStatus | null;
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    exploring: !!data?.exploring,
    canExplore: !!data?.can_explore,
    realNurseryId: data?.real_nursery_id ?? null,
    realNurseryName: data?.real_nursery_name ?? null,
    realPlan: data?.real_plan ?? null,
    isLoading,
  };
}

export function useEnterExploreMode() {
  const queryClient = useQueryClient();
  const { refreshProfile } = useAuth();
  return useMutation({
    mutationFn: async () => {
      // Repair the rare verified-signup race before opening Explore. The
      // server repeats this check transactionally, so both this UI and any
      // future callers are protected from nursery-less account dead ends.
      const { error: setupError } = await supabase.rpc("ensure_my_signup_account" as never);
      if (setupError) throw setupError;

      const { error } = await supabase.rpc("enter_explore_mode" as never);
      if (error) throw error;
    },
    onSuccess: async () => {
      // The signed-in person's profile decides which nursery is active and
      // whether setup is finished, so it must be re-read before any routing
      // decision is made.
      await refreshProfile();
      await queryClient.invalidateQueries();
    },
  });
}

export function useExitExploreMode() {
  const queryClient = useQueryClient();
  const { refreshProfile } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("exit_explore_mode" as never);
      if (error) throw error;
    },
    onSuccess: async () => {
      await refreshProfile();
      await queryClient.invalidateQueries();
    },
  });
}
